import React, { useState, useEffect, useRef, FC, useCallback } from 'react';

import 'react-image-gallery/styles/css/image-gallery.css';
import ReactImageGallery, { ReactImageGalleryProps } from 'react-image-gallery';

import { ChevronLeft, ChevronRight } from '@assets/icons';
import { FALLBACK_IMAGE_URL } from '@constants/constants';
import { useBreakpoint } from '@contexts/breakpoints';
import { Entity } from '@components/views/hotel/detail/components/details-galleries-modal/types';
import { ModalCloseButton } from '@components/Modal/utils';

import { getDisplayName, getDisplayText } from './utils';
import {
  ImageViewer,
  NavArrowsContainer,
  Arrow,
  HorizontalScrollableGalleriesList,
  GalleryListItemTitle,
  Title,
  CurrentSlideInfoDesktop,
  CurrentSlideInfoMobile,
  CloseImageViewerButton,
  GalleryHeaderWrapper,
} from './GalleryStyles';

type ImageGalleryProps = Omit<ReactImageGalleryProps, 'items'> & {
  galleries: { [key: string]: ReactImageGalleryProps['items'] };
  title: string;
  horizontalPadding?: `${number}%`;
  closeGallery?: () => void;
  activeGallery?: number;
  hideCategoryAll?: boolean;
  index?: number;
  entityType?: Entity;
};

const EMPTY_GALLERY_IMAGE_SET = [{ original: FALLBACK_IMAGE_URL }];

export const useGalleriesKeys = (
  galleries: ImageGalleryProps['galleries'],
  hideCategoryAll?: boolean
) => {
  const [keys] = useState(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { all, ...others } = galleries;
    const keys = Object.keys(others);
    return hideCategoryAll ? keys : ['all', ...keys];
  });
  return keys;
};

const ImageGallery: FC<ImageGalleryProps> = ({
  galleries,
  title,
  horizontalPadding = '9.24%' as `${number}%`,
  closeGallery,
  hideCategoryAll,
  index,
  entityType,
  ...rest
}) => {
  const { xl: isDesktop } = useBreakpoint();
  const keys = useGalleriesKeys(galleries, hideCategoryAll);
  const [activeGallery, setActiveGallery] = useState(0);
  useEffect(() => {
    const body = document.querySelector('body');
    if (body) {
      const overflow = getComputedStyle(body).overflow;
      body.style.overflow = 'hidden';
      return () => {
        body.style.overflow = overflow;
      };
    }
  }, []);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const galleryRef = useRef<ReactImageGallery | null>(null);
  const items = galleries[keys[activeGallery]];
  const navigate = useCallback((index: number) => {
    galleryRef.current?.slideToIndex(
      galleryRef.current?.getCurrentIndex() + index
    );
  }, []);
  useEffect(() => {
    setCurrentSlideIndex(index || 0);
  }, [activeGallery, index]);

  return (
    <ImageViewer horizontalPadding={horizontalPadding}>
      <GalleryHeaderWrapper>
        <Title>{title}</Title>
        {isDesktop && entityType === 'hotel' ? (
          <ModalCloseButton onClick={closeGallery} paletteColor='white' />
        ) : null}
      </GalleryHeaderWrapper>
      {items.length ? (
        <CurrentSlideInfoMobile>
          <span>{getDisplayName(keys[activeGallery])}</span>
          &nbsp;
          <span>
            {currentSlideIndex + 1}/{items.length}
          </span>
        </CurrentSlideInfoMobile>
      ) : null}

      <CloseImageViewerButton
        onClick={closeGallery}
        noLabel
        paletteColor='white'
      />

      <HorizontalScrollableGalleriesList activeItem={`${activeGallery}`}>
        {keys.map((categoryName, galleryIndex) => (
          <GalleryListItemTitle
            key={categoryName}
            data-item={galleryIndex.toString()}
            onClick={(e) => {
              e.currentTarget?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'center',
              });
              setActiveGallery(galleryIndex);
            }}
          >
            {getDisplayText(categoryName, galleries[categoryName]?.length)}
          </GalleryListItemTitle>
        ))}
      </HorizontalScrollableGalleriesList>
      {items.length ? (
        <CurrentSlideInfoDesktop>
          <span>{getDisplayName(keys[activeGallery])}</span>

          <span>
            {currentSlideIndex + 1}/{items.length}
          </span>
        </CurrentSlideInfoDesktop>
      ) : null}
      <ReactImageGallery
        key={keys[activeGallery]}
        ref={(gallery) => {
          galleryRef.current = gallery;
        }}
        showPlayButton={false}
        showFullscreenButton={false}
        {...rest}
        showNav={false}
        items={items.length ? items : EMPTY_GALLERY_IMAGE_SET}
        startIndex={index || 0}
        onSlide={setCurrentSlideIndex}
        onErrorImageURL={FALLBACK_IMAGE_URL}
        lazyLoad
      />
      {items.length > 1 ? (
        <NavArrowsContainer>
          <Arrow direction='prev' onClick={() => navigate(-1)}>
            <ChevronLeft />
          </Arrow>
          <Arrow direction='next' onClick={() => navigate(1)}>
            <ChevronRight />
          </Arrow>
        </NavArrowsContainer>
      ) : null}
    </ImageViewer>
  );
};

export default ImageGallery;
